// StatusLegend — a compact row of labeled chips that self-documents the
// color / emoji / arrow encodings a page uses. So a non-finance board member
// never has to guess what 🔴 or ▲ means.
//
// Usage:
//   <StatusLegend
//     items={[
//       { swatch: "🔴", label: "Act now" },
//       { swatch: "🟠", label: "Watch" },
//     ]}
//   />
//
// `swatch` is either a short string (emoji / arrow glyph like "▲") or a color
// token name to render as a colored dot.

import type { ReactNode } from "react";

export interface LegendItem {
  /** An emoji/glyph string ("🔴", "▲", "→"), OR a color for a dot (see `dot`). */
  swatch?: ReactNode;
  /** When set, render a colored dot instead of a glyph. Tailwind bg class. */
  dot?: string;
  label: string;
}

interface StatusLegendProps {
  items: LegendItem[];
  /** Optional leading caption, e.g. "Legend". */
  caption?: string;
  className?: string;
}

export default function StatusLegend({
  items,
  caption,
  className,
}: StatusLegendProps) {
  return (
    <div
      className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-theme-muted ${className ?? ""}`}
      aria-label={caption ?? "Legend"}
    >
      {caption && (
        <span className="font-semibold uppercase tracking-wider text-theme-muted">
          {caption}
        </span>
      )}
      {items.map((it, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {it.dot ? (
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${it.dot}`}
              aria-hidden="true"
            />
          ) : (
            <span aria-hidden="true">{it.swatch}</span>
          )}
          <span className="text-theme-secondary">{it.label}</span>
        </span>
      ))}
    </div>
  );
}
